// The Summary tab: the answer in a sentence, the totals, sales by day, what is
// worth a look, and where the sales came from.
import { useMemo, useState } from "react";
import { Drawer } from "@mui/material";
import { getCustomerHealth, getSalesmanScorecard } from "../../distflowAPI/salesAnalyticsApi";
import Answer from "./Answer";
import BreakdownCards from "./BreakdownCards";
import Highlights from "./Highlights";
import SalesExplorer from "./SalesExplorer";
import SalesTrend from "./SalesTrend";
import { summaryAnswer } from "./answers";
import { customerLevelFilters, dimensionOfKey } from "./drillDown";
import { count, money, pct, shortRange, signedPct } from "./format";
import { accountTypesHighlight, categoriesHighlight, customersHighlight, growthHighlight, salesmanHighlight } from "./highlightRules";
import useSideReport from "./useSideReport";

const LOW_COST_COVERAGE = 95;
const number = (value) => Number(value || 0);

/** Colour a change only when it moves 10% or more. */
function changeTone(percent) {
  if (percent == null || Math.abs(number(percent)) < 10) return "flat";
  return number(percent) > 0 ? "gain" : "decline";
}
function signedCount(value) {
  const amount = number(value);
  return `${amount > 0 ? "+" : amount < 0 ? "−" : ""}${count(Math.abs(amount))}`;
}

function Totals({ report }) {
  const summary = report.summary || {};
  const hasComparison = Boolean(report.comparison);
  const range = shortRange(report.date_range?.start_date, report.date_range?.end_date);
  const before = hasComparison ? shortRange(report.comparison.start_date, report.comparison.end_date) : "";
  const previousPerDay = summary.previous_sales_per_open_day != null ? number(summary.previous_sales_per_open_day)
    : summary.previous_open_days ? number(summary.previous_total_sales) / number(summary.previous_open_days) : null;
  const rows = [
    { label: "Sales", value: money(summary.total_sales), change: signedPct(summary.sales_change_percent), tone: changeTone(summary.sales_change_percent),
      from: summary.previous_total_sales != null ? `from ${money(summary.previous_total_sales)}` : null },
    summary.open_days != null && { label: "Open days", value: count(summary.open_days),
      change: summary.previous_open_days != null ? signedCount(number(summary.open_days) - number(summary.previous_open_days)) : null, tone: "flat",
      from: summary.previous_open_days != null ? `from ${count(summary.previous_open_days)}` : null },
    summary.open_days != null && { label: "Per open day", value: summary.sales_per_open_day == null ? "—" : money(summary.sales_per_open_day),
      change: signedPct(summary.sales_per_open_day_change_percent), tone: changeTone(summary.sales_per_open_day_change_percent),
      from: previousPerDay != null ? `from ${money(previousPerDay)}` : null },
    summary.gross_profit !== undefined && { label: "Gross profit", value: summary.gross_profit == null ? "—" : money(summary.gross_profit),
      change: signedPct(summary.gross_profit_change_percent), tone: changeTone(summary.gross_profit_change_percent),
      from: summary.margin_percent != null ? `${pct(summary.margin_percent)} margin` : "no costs recorded", keep: true },
    { label: "Orders", value: count(summary.total_orders), change: signedPct(summary.total_orders_change_percent), tone: changeTone(summary.total_orders_change_percent),
      from: summary.previous_total_orders != null ? `from ${count(summary.previous_total_orders)}` : null },
    { label: "Average order", value: money(summary.average_order_size), change: signedPct(summary.average_order_size_change_percent),
      tone: changeTone(summary.average_order_size_change_percent),
      from: summary.previous_average_order_size != null ? `from ${money(summary.previous_average_order_size)}` : null },
    { label: "Active customers", value: count(summary.total_customers),
      change: summary.previous_total_customers != null ? signedCount(number(summary.total_customers) - number(summary.previous_total_customers)) : null, tone: "flat",
      from: summary.previous_total_customers != null ? `from ${count(summary.previous_total_customers)}` : null },
  ].filter(Boolean);
  const coverage = summary.cost_coverage_percent;
  return <section className="sa-box sa-totals" aria-labelledby="sa-totals-title">
    <h3 className="sa-box-title" id="sa-totals-title">Totals</h3>
    <table aria-labelledby="sa-totals-title">
      <thead><tr><th scope="col">Measure</th><th scope="col">{range}</th>{hasComparison && <th scope="col">vs {before}</th>}</tr></thead>
      <tbody>{rows.map((row) => <tr key={row.label}>
        <th scope="row">{row.label}</th>
        <td className="sa-totals-value">{row.value}{!hasComparison && row.keep && row.from && <small className="sa-totals-note-inline">{row.from}</small>}</td>
        {hasComparison && <td className="sa-totals-change">
          <b className={`sa-delta--${row.tone}`}>{row.change ?? "—"}</b>
          {row.from && <small>{row.from}</small>}
        </td>}
      </tr>)}</tbody>
    </table>
    {coverage != null && number(coverage) < LOW_COST_COVERAGE && <p className="sa-totals-note">Cost is known for {pct(coverage, 0)} of item sales, so gross profit leaves some out.</p>}
  </section>;
}

export default function Summary({ report, view, applied, filters, ready, onChange, onDrill, onOpenCustomer, onOpenProduct, onOpenSalesman }) {
  const [movement, setMovement] = useState("all");
  const hasComparison = Boolean(report.comparison);

  const healthParams = useMemo(() => ({
    as_of: report.date_range?.end_date, territory_id: applied.territory_id, filters: customerLevelFilters(filters),
  }), [report.date_range?.end_date, applied.territory_id, filters]);
  const scorecardParams = useMemo(() => ({
    start_date: applied.start_date, end_date: applied.end_date, territory_id: applied.territory_id,
    comparison: applied.comparison, comparison_start_date: applied.comparison_start_date, comparison_end_date: applied.comparison_end_date,
  }), [applied]);
  // Side reports wait for the main report, so the numbers on screen come first.
  const health = useSideReport(getCustomerHealth, healthParams, ready && Boolean(healthParams.as_of));
  const scorecard = useSideReport(getSalesmanScorecard, scorecardParams, ready);

  const items = [
    customersHighlight(health.data),
    categoriesHighlight(report, health.data),
    salesmanHighlight(report, scorecard.data),
    accountTypesHighlight(report),
  ].filter(Boolean);
  if (items.length < 2 && !health.loading) items.push(growthHighlight(report));

  function act(action) {
    if (action.tab) { onChange({ tab: action.tab }); return; }
    if (action.rep) { onOpenSalesman?.(action.rep); return; }
    if (action.breakdown) {
      setMovement(action.movement || "all");
      onChange({ by: action.breakdown });
    }
  }

  // Customers, products and salesmen open their own details; anything else focuses the page.
  function openRow(row) {
    const dimension = dimensionOfKey(row.key);
    if (dimension === "customer" && onOpenCustomer) onOpenCustomer(row.key);
    else if (dimension === "product" && onOpenProduct) onOpenProduct(row.key);
    else if (dimension === "salesman" && onOpenSalesman) onOpenSalesman(row.key);
    else { onChange({ by: null }); onDrill?.(row); }
  }
  const openBreakdown = (by) => { setMovement("all"); onChange({ by }); };
  const comparisonDates = hasComparison ? shortRange(report.comparison.start_date, report.comparison.end_date) : undefined;

  return <>
    <Answer answer={summaryAnswer(report)} />
    <div className="sa-summary-grid">
      <Totals report={report} />
      <SalesTrend id="sales-analytics-trend" trend={report.trend} comparisonLabel={comparisonDates} />
    </div>
    <Highlights items={items} loading={health.loading || scorecard.loading} onAction={act} />
    <BreakdownCards report={report} onOpen={openBreakdown} />
    {view.by && <Drawer anchor="right" open onClose={() => onChange({ by: null })} sx={{ zIndex: 1300 }}
      PaperProps={{ className: "sa-breakdown-drawer sa-below-appbar", role: "dialog", "aria-labelledby": "sa-breakdown-title" }}>
      <SalesExplorer report={report} dimension={view.by} onDimensionChange={openBreakdown}
        movement={movement} onMovementChange={setMovement} onDrill={openRow} onClose={() => onChange({ by: null })} />
    </Drawer>}
  </>;
}
